/**
 * @fileoverview A React component that displays a consent form with checkboxes for user consent.
 * The component allows users to select their consent for various terms and conditions.
 *
 * @example
 * function ParentComponent() {
 *   const [consent, setConsent] = useState({
 *     cookies: false,
 *     emails: false,
 *     tracking: false,
 *     acknowledge: false,
 *     tos: false
 *   });
 *
 *   return <ConsentForm consent={consent} handleConsentChange={handleConsentChange} handleContinue={handleContinue} allConsentsGiven={allConsentsGiven} />;
 * }
 */

import { useState } from 'react'
import CrossIcon from '../assets/cross-small.svg'


/**
 * @interface ConsentFormProps
 * @description Props for the ConsentForm component.
 * @property {object} consent - The consent object with boolean values for each consent item.
 * @property {(event: React.ChangeEvent<HTMLInputElement>) => void} handleConsentChange - The function to call when a consent item is changed.
 * @property {() => void} handleContinue - The function to call when all consents are given.
 * @property {boolean} allConsentsGiven - A boolean indicating if all consents are given.
 */
interface ConsentFormProps {
    consent: {
        cookies: boolean
        emails: boolean
        tracking: boolean
        acknowledge: boolean
        tos: boolean
    }
    handleConsentChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    handleContinue: () => void
    allConsentsGiven: boolean
}

/**
 * @interface ConsentCheckboxProps
 * @description Props for the ConsentCheckbox component.
 * @property {string} name - The name of the consent item.
 * @property {(event: React.ChangeEvent<HTMLInputElement>) => void} onChange - The function to call when the consent item is changed.
 * @property {boolean} checked - A boolean indicating if the consent item is checked.
 * @property {JSX.Element} content - The content of the consent item.
 * @property {boolean} error - A boolean indicating if there is an error with the consent item.
 */
interface ConsentCheckboxProps {
    name: string
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    checked: boolean
    content: JSX.Element
    error: boolean
}

/**
 * @function classNames
 * @description Utility function to join class names with spaces.
 * @param {...string} classes - The class names to join.
 * @returns {string} - The joined class names.
 */
function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ')
}

/**
 * @function ConsentForm
 * @description Component for displaying a consent form with checkboxes for user consent.
 * @returns {JSX.Element | null} - The ConsentForm component.
 */
export function ConsentForm({
    consent,
    handleConsentChange,
    handleContinue,
    allConsentsGiven,
}: ConsentFormProps) {
    const [errors, setErrors] = useState<Record<string, boolean>>({})

    function onContinue() {
        const newErrors: Record<string, boolean> = {}
        if (!consent.cookies) {
            newErrors.cookies = true
        }
        if (!consent.emails) {
            newErrors.emails = true
        }
        if (!consent.tracking) {
            newErrors.tracking = true
        }
        if (!consent.acknowledge) {
            newErrors.acknowledge = true
        }
        if (!consent.tos) {
            newErrors.tos = true
        }
        setErrors(newErrors)

        const isValid = Object.keys(newErrors).length === 0

        if (isValid) {
            handleContinue()
        }
    }

    return (
        <div className="relative mt-8 space-y-6">
            <p>
                Before you start using the Novoic Dashboard, we need your consent for a few things.
                Please review each item below and tick the checkboxes to confirm your consent.
            </p>
            <div className="flex flex-col border-gray-200 border divide-y rounded-lg overflow-hidden">
                <ConsentCheckbox
                    name="cookies"
                    onChange={handleConsentChange}
                    checked={consent.cookies}
                    error={errors.cookies}
                    content={
                        <>
                            <b className="font-medium">
                                I agree to the use of cookies on the Novoic Dashboard.
                            </b>{' '}
                            <span>
                                These cookies are necessary for the Novoic Dashboard to function
                                properly and enhance your user experience.
                            </span>
                        </>
                    }
                />
                <ConsentCheckbox
                    name="emails"
                    onChange={handleConsentChange}
                    checked={consent.emails}
                    error={errors.emails}
                    content={
                        <>
                            <b className="font-medium">
                                I consent to receiving emails from Novoic.
                            </b>{' '}
                            <span>
                                Those emails may include reminders and reports, important updates,
                                news about the Novoic Dashboard and other relevant information.
                            </span>
                        </>
                    }
                />
                <ConsentCheckbox
                    name="tracking"
                    onChange={handleConsentChange}
                    checked={consent.tracking}
                    error={errors.tracking}
                    content={
                        <>
                            <b className="font-medium">
                                I permit Novoic to track and analyze my usage of the Novoic
                                Dashboard.
                            </b>{' '}
                            <span>
                                All data collected will be used responsibly for the purpose of
                                improving the performance and quality.
                            </span>
                        </>
                    }
                />
                <ConsentCheckbox
                    name="acknowledge"
                    onChange={handleConsentChange}
                    checked={consent.acknowledge}
                    error={errors.acknowledge}
                    content={
                        <>
                            <b className="font-medium">
                                I confirm that I have the necessary education, training, and
                                experience to use the Novoic Dashboard.
                            </b>{' '}
                            <span>
                                I acknowledge and agree to use it according to all applicable
                                policies, procedures, and regulations. I understand that Novoic does
                                not provide medical advice or health-related recommendations.
                            </span>
                        </>
                    }
                />
                <ConsentCheckbox
                    name="tos"
                    onChange={handleConsentChange}
                    checked={consent.tos}
                    error={errors.tos}
                    content={
                        <b className="font-medium">
                            I have read and accepted Novoic&apos;s{' '}
                            <a
                                href="https://novoic.com/dashboard/tos"
                                target="_blank"
                                rel="noreferrer"
                                className="text-indigo-700 underline underline-offset-2"
                            >
                                Terms of Use
                            </a>{' '}
                            and{' '}
                            <a
                                href="https://novoic.com/dashboard/privacy"
                                target="_blank"
                                rel="noreferrer"
                                className="text-indigo-700 underline underline-offset-2"
                            >
                                Privacy Policy
                            </a>{' '}
                            for Novoic Dashboard.
                        </b>
                    }
                />
            </div>
            {Object.keys(errors).length > 0 && !allConsentsGiven && (
                <div className="rounded-md bg-red-50 p-4 mt-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <CrossIcon className="h-5 w-5 fill-red-200" aria-hidden="true" />
                        </div>
                        <div className="ml-3 flex-1 md:flex md:justify-between">
                            <p className="text-sm text-red-800 font-medium">
                                Tick the checkboxes to confirm your consent to proceed.
                            </p>
                        </div>
                    </div>
                </div>
            )}
            <div>
                <button
                    type="button"
                    onClick={onContinue}
                    className={`group relative flex w-full justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-base font-medium text-white disabled:opacity-75 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                >
                    Continue
                </button>
            </div>
        </div>
    )
}

/**
 * @function ConsentCheckbox
 * @description Component for displaying a checkbox with a label for user consent.
 * @returns {JSX.Element | null} - The ConsentCheckbox component.
 */
function ConsentCheckbox({ name, onChange, checked, content, error }: ConsentCheckboxProps) {
    return (
        <label
            className={classNames(
                'flex p-4 cursor-pointer text-sm transition-colors',
                checked ? 'bg-indigo-50' : error ? 'bg-red-50' : 'bg-white hover:bg-gray-50'
            )}
        >
            <input
                type="checkbox"
                className={classNames(
                    'mr-4 rounded text-indigo-600 focus:ring-indigo-500',
                    checked ? 'border-indigo-600' : error ? 'border-red-600' : 'border-gray-300'
                )}
                name={name}
                checked={checked}
                onChange={onChange}
            />
            <span className="-mt-1">{content}</span>
        </label>
    )
}
